/**
 * NetX extensions to jQuery.
 * It seems necessary to install these before Backbone is loaded in order to get
 * these new functions copied onto the scoped jQuery instances provided by Backbone.View.
 *
 * NOTE: Be sure to document new items in https://confluence.netx.net/display/DEV/X7+Library+Overrides%2C+Extensions+and+Mixins
 */
import jQuery from 'jquery';

const _show = jQuery.fn.show;
const _val = jQuery.fn.val;

/**
 * Special event to listen for destroyed elements
 */
jQuery.event.special.destroyed = {
	remove(o) {
		if (o.handler) {
			o.handler.apply(this, arguments);
		}
	},
};

jQuery.extend(jQuery.expr[':'], {
	/**
	 * Data pseudo selector
	 */
	data: jQuery.expr.createPseudo(function (dataName) {
		return function (elem) {
			return !!jQuery.data(elem, dataName);
		};
	}),
});

//
// Added functions:
//
jQuery.fn.extend({
	//
	// A HACK to support bootstrap-select: call refresh on the selectpicker plugin when we change the value.
	// (Really this should be done inside the plugin with a mutation observer).
	//
	val(val) {
		// Super
		var ret = _val.apply(this, arguments);

		// If we have a selectpicker and we are trying to set a value (not get)
		if (this.data('selectpicker')) {
			if (!_.isUndefined(val)) {
				this.selectpicker('render');
			}
		}
		return ret;
	},
	/**
	 * With $.nxVal, data-nx-value can override the nominal values of form elements.
	 */
	nxVal() {
		var d = this.data('nx-value');
		if (typeof d !== 'undefined') {
			return d;
		} else {
			return this.val();
		}
	},
	/**
	 * turn the values of a set of checked checkboxes into an array --
	 * you'd think jQuery would just do that, but no.
	 * http://stackoverflow.com/questions/19766044/best-way-to-get-all-selected-checkboxes-values-in-jquery
	 */
	boxesToVal() {
		return this.find(':checked')
			.map(function () {
				return this.value;
			})
			.get();
	},
	/**
	 * NetX preferred method to enable/disable an element
	 *
	 * Disable element.
	 * @returns {object} The jQuery object
	 */
	disable() {
		this.attr('disabled', true);
		this.addClass('disabled');
		return this;
	},
	/**
	 * NetX preferred method to enable/disable an element
	 *
	 * Enable element.
	 * @returns {object} The jQuery object
	 */
	enable() {
		this.attr('disabled', false);
		this.removeClass('disabled');
		return this;
	},
	/**
	 * NetX preferred method to enable/disable an element
	 *
	 * Enable/Disable element based on the bool passed.
	 * @param {bool} bool True or false
	 * @returns {object} The jQuery object
	 */
	enableIf(bool) {
		return bool ? this.enable() : this.disable();
	},
	/**
	 * Check if typeahead menu is open
	 * @method
	 * @return {boolean}
	 */
	hasActiveTypeahead() {
		var tt = this.data('ttTypeahead');
		var $menu = tt && tt.menu.$node;
		return $menu && $menu.hasClass('tt-open') && !$menu.hasClass('tt-empty');
	},
	/**
	 * Check for collision between two elements
	 * @param {object} b The jQuery object to test collision with
	 * @returns {bool} Whether or not element has collision
	 */
	hasCollision(b) {
		var aTop = this.offset().top,
			aLeft = this.offset().left,
			bTop = b.offset().top,
			bLeft = b.offset().left;

		return !(
			aTop + this.outerHeight() < bTop ||
			aTop > bTop + b.outerHeight() ||
			aLeft + this.outerWidth() < bLeft ||
			aLeft > bLeft + b.outerWidth()
		);
	},
	/**
	 * Check if event is already bound to an element
	 * @param {string} type The event type
	 * @param {string} namespace The event namespace
	 * @param {function} fn The function tied to the event
	 * @returns {object} The jQuery object
	 */
	isBound(type, namespace, fn) {
		var data = jQuery._data(this[0], 'events')[type];
		if (typeof namespace === 'undefined') {
			namespace = '';
		}
		if (data === undefined || data.length === 0) {
			return false;
		}
		if (typeof fn !== 'undefined') {
			return -1 !== jQuery.inArray(fn, data);
		}
		var found = false;
		jQuery.each(data, function (d) {
			if (typeof d === 'object') {
				if (
					typeof d.type !== 'undefined' &&
					d.type == type &&
					typeof d.namespace !== 'undefined' &&
					d.namespace == namespace
				) {
					found = true;
					return false;
				}
			}
		});
		return found;
	},
	/**
	 * Checks whether or not an element is hidden.
	 * @returns {bool} True if hidden, else false
	 */
	isHidden() {
		return !this.is(':visible'); // http://stackoverflow.com/questions/178325/testing-if-something-is-hidden-with-jquery
	},
	/**
	 * Checks whether or not an element is within a modal.
	 * @returns {bool} True if modal, else false
	 */
	isModal() {
		return Boolean(this.closest('.modal').length);
	},

	/**
	 * Checks the boolean data-nx-ignore condition of this element, for a particular key.
	 * If not found on the current element, we search for it up the tree using .closest()
	 * We can use this for overriding NX behaviours on various elements.
	 *
	 * So far the supported keys are all used by FormView form processing:
	 *
	 *    'change': (was data-nx-form-ignore) FormView ignores change events on fields for which this is set.
	 *    'validate': (was data-nx-ignore-field) Form validation does not validate form fields for which this is set.
	 *    'matrix': FormView's read-only mode will not disable form fields for which this is set.
	 *
	 * However, this is intended to be extensible for overriding any Netx.View behaviors that need it.
	 *
	 * @param {string} key
	 * @returns {bool}
	 */
	nxIgnores(key) {
		// Search for our key in this list of deprecations:
		var deprecations = {
			change: 'form-ignore',
			validation: 'ignore-field',
		};

		var selector = '[data-nx-ignore~="' + key + '"]';
		// hopefully undef.
		var dep = deprecations[key];
		if (dep) {
			selector += ',[data-nx-' + dep + ']';
		}

		var el = this.closest(selector);
		if (el.length) {
			return true;
		}

		// Okay, deprecation is over, the rest is more logical.
		// Split by whitespace:
		var keys = this.nxData('ignore');
		if (keys) {
			keys = keys.split(/\s+/);
			return keys.indexOf(key) >= 0;
		}

		return false;
	},

	/**
	 * Adds a key to the data-nx-ignore attribute
	 * @return this
	 */
	nxIgnore(key) {
		let keys = this.nxData('ignore') || '';
		keys = _.uniq(keys.split(/\s+/).concat(key)).join(' ');
		this.nxData('ignore', keys);
		return this;
	},

	/**
	 * Removes a key from the data-nx-ingore attribute
	 * @return this
	 */
	nxUnignore(key) {
		let keys = this.nxData('ignore');
		if (keys == null) return this;

		keys = keys
			.split(/\s+/)
			.filter((k) => k !== key)
			.join(' ');
		this.nxData('ignore', keys);

		return this;
	},

	/**
	 * Gets/sets a data-nx-* key (X7-specific element data.)
	 */
	nxData(key, newVal) {
		// Return all `nx-<key>`
		if (!key) {
			return _.pick(this.data(), (v, k) => k.match(/^nx/));
		}

		// Set new value
		if (newVal != null) {
			return this.data(`nx-${key}`, newVal);
		}

		// Return specific `nx-<key>`
		return this.data(`nx-${key}`);
	},

	/**
	 * Removes a data-nx-* key.
	 */
	nxRemoveData(key) {
		key = 'nx-' + key;
		// https://api.jquery.com/removeData/
		this.removeData(key);
		this.removeAttr(key);
		return;
	},
	/**
	 * wrap $.show() to also remove our deprecated 'hide' class
	 */
	show() {
		if (this.hasClass('hide')) {
			// TODO: Discuss this deprecation
			// console.warn('v8 "hide" class is deprecated, gone in 8.10');
			this.removeClass('hide');
		}
		return _show.apply(this, arguments);
	},
	/**
	 * turn an array value into the state of a set of checkboxes
	 */
	valToBoxes(val) {
		_.each(this.find('checkbox'), function (c) {
			jQuery(c).prop('checked', val.indexOf(c.value) > -1);
		});
	},
	/**
	 * Set or get the zIndex of an element
	 * Brought over from jQuery UI
	 * http://jqueryui.com/
	 * @param {integer} zIndex The zIndex to set for the element (if omitted it will get the zIndex of the element)
	 * @returns {*} Either the jQuery object or the zIndex of the element
	 */
	zIndex(zIndex) {
		if (zIndex !== undefined) {
			return this.css('zIndex', zIndex);
		}

		if (this.length) {
			var elem = $(this[0]),
				position,
				value;
			while (elem.length && elem[0] !== document) {
				// Ignore z-index if position is set to a value where z-index is ignored by the browser
				// This makes behavior of this function consistent across browsers
				// WebKit always returns auto if the element is positioned
				position = elem.css('position');
				if (
					position === 'absolute' ||
					position === 'relative' ||
					position === 'fixed'
				) {
					// IE returns 0 when zIndex is not specified
					// other browsers return a string
					// we ignore the case of nested elements with an explicit value of 0
					// <div style="z-index: -10;"><div style="z-index: 0;"></div></div>
					value = parseInt(elem.css('zIndex'), 10);
					if (!isNaN(value) && value !== 0) {
						return value;
					}
				}
				elem = elem.parent();
			}
		}

		return 0;
	},
	/**
	 * Purge a selection.  That is, remove from this jQuery array any selection
	 * that is a descendant of one of the arguments.  Useful for removing the contents
	 * of child views from a parent view's selection.
	 *
	 * This is different from jQuery's :not() selector, which can only filter a
	 * selection based on its own contents.  purge() specifically removes items in the
	 * selection array which have one of the provided arguments as an ancestor.
	 *
	 * Parameters are either a set of DOM elements, or a single array containing such a set of elements (ala jQuery)
	 */
	purge() {
		// flexible args
		if (arguments.length === 0) {
			return this;
		} // an optimization for the most common case

		if (arguments.length === 1 && _.isArray(arguments[0])) {
			// if we were passed an array,
			return this.purge.apply(this, arguments[0]); // convert that array to arguments
		}

		var args = _.toArray(arguments);
		var result = this.filter(function (idx, el) {
			var allowed = true;
			_.each(args, function (arg) {
				if (allowed === false) {
					return;
				} // optimization
				if ($(arg).has(el).length) {
					allowed = false;
				}
			});
			return allowed;
		});
		return result;
	},
});

// Turn tap events into clicks
jQuery.event.special.tapToClick = {
	setup: function (data, namespaces) {
		var $elem = $(this);
		$elem
			.bind('touchstart', $.event.special.tapToClick.handler)
			.bind('touchmove', $.event.special.tapToClick.handler)
			.bind('touchend', $.event.special.tapToClick.handler);
	},
	teardown: function (namespaces) {
		var $elem = $(this);
		$elem
			.unbind('touchstart', $.event.special.tapToClick.handler)
			.unbind('touchmove', $.event.special.tapToClick.handler)
			.unbind('touchend', $.event.special.tapToClick.handler);
	},
	handler: function (event) {
		var $elem = $(this);
		$elem.data(event.type, 1);
		if (event.type === 'touchend' && !$elem.data('touchmove')) {
			event.preventDefault();
			this.click();
		} else if ($elem.data('touchend')) {
			$elem.removeData('touchstart touchmove touchend');
		}
	},
};

export default jQuery;
